import { HelpCircle } from "lucide-react";

/**
 * The four server-owner tools' only permanent way into their own tour.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS TO CLOSE
 *
 * `ToolWalkthrough` decides manual mode by the *presence* of `openSignal`, and
 * these four islands pass it. Manual mode switches off both of the entry points
 * every creator tool relies on: the first-visit auto-start, and the floating
 * "Show tour" pill. That is correct — a dimmer thrown over a stranger's
 * 300-resource folder is the failure the prop was added to prevent — but it
 * left the tours with exactly one caller each: the sample-pack button, and its
 * own panel is gated on there being no report yet.
 *
 * So the arithmetic was: a server owner opens the tool, drops the
 * `resources/` folder that is the tool's whole purpose, and there is no tour
 * and no way to ask for one, ever. The word "tour" did not appear on screen
 * until after a sample had been loaded. The one user who could reach a tutorial
 * was the one who had already been shown what the tool does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS NOT GATED ON ANYTHING
 *
 * Not on `demo`: the user who brought their own folder is the user who most
 * needs it. Not on the empty state: that panel disappears the moment a report
 * exists, which is the moment the tour has something real to point at. Not on
 * `dismissed`: a person pressing this after finishing the tour once is asking
 * for it again, which is the replay defect the pill still has.
 *
 * A tour opened over the reader's own report spotlights their own findings —
 * `ToolWalkthrough` resolves every selector at open time, and a step whose
 * anchor is missing degrades to a centred tooltip rather than throwing. So the
 * cost of the ungated button is at worst a step that reads slightly generically
 * over somebody else's data, against a tutorial that is otherwise unreachable.
 *
 * Deliberately not the floating pill: this sits in the tool's own intake row,
 * where a first-time visitor is already looking for the control that starts
 * things, and it does not have to share the z-stack with the wrench launcher.
 */
export function ShowTourButton({
  onClick,
  label = "Show me how this works",
  testId,
}: {
  onClick: () => void;
  /** Overridable for a tool whose intake row is already crowded. Keep it short. */
  label?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId ?? "free-tool-show-tour"}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
    >
      <HelpCircle className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

export default ShowTourButton;
